'use client';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@pixel-party/Accordion/v2';
import {Elevation} from '@pixel-party/Elevation/v2';
import {Typography} from '@pixel-party/Typography/v2';
import {Trans, useTranslation} from 'react-i18next';
import styles from './VerificationDocuments.module.scss';

function VerificationDocuments(): React.JSX.Element {
  const {t} = useTranslation();

  return (
    <div className={styles.container}>
      <Typography
        use="headline2"
        className={styles.headline}
      >
        {t('forms.addressVerification.verificationDocuments.headline')}
      </Typography>
      <Elevation z={2}>
        <Accordion
          className={styles.accordion}
          handle={
            <AccordionSummary
              className={styles.summary}
              text={t(
                'forms.addressVerification.verificationDocuments.residential.label',
              )}
            />
          }
        >
          <AccordionDetails className={styles.details}>
            <Typography use="headline3">
              {t(
                'forms.addressVerification.verificationDocuments.residential.option1.title',
              )}
            </Typography>
            <p>
              <Trans
                i18nKey="forms.addressVerification.verificationDocuments.residential.option1.validId"
                components={{bold: <strong />}}
              >
                A <strong>valid, US or foreign&nbsp;government issued</strong>{' '}
                identification (such as a driving license, identification card
                or passport information page) that matches:
              </Trans>
            </p>
            <ul>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.residential.option1.nameOnAccount"
                  components={{bold: <strong />}}
                >
                  The name on the account <strong>AND</strong>
                </Trans>
              </li>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.residential.option1.accountAddress"
                  components={{bold: <strong />}}
                >
                  The GFiber account address listed <strong>OR</strong>
                </Trans>
              </li>
            </ul>
            <Typography use="headline3">
              {t(
                'forms.addressVerification.verificationDocuments.residential.option2.title',
              )}
            </Typography>
            <ol>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.residential.option2.validId"
                  components={{bold: <strong />}}
                >
                  A <strong>valid, US or foreign government issued</strong>{' '}
                  identification that matches
                </Trans>
                <ul>
                  <li>
                    <Trans
                      i18nKey="forms.addressVerification.verificationDocuments.residential.option2.nameOnAccount"
                      components={{bold: <strong />, italic: <em />}}
                    >
                      The name on the account (<em>not</em> the GFiber account
                      address listed) <strong>AND</strong>
                    </Trans>
                  </li>
                </ul>
              </li>
              <li>
                {t(
                  'forms.addressVerification.verificationDocuments.residential.option2.billingDoc',
                )}
                <ul>
                  <li>
                    <Trans
                      i18nKey="forms.addressVerification.verificationDocuments.residential.option2.billDetail"
                      components={{bold: <strong />}}
                    >
                      A bill from the last 60 days, such as a bank statement or
                      utility bill, addressed to you and that lists the GFiber
                      account address <strong>OR</strong>
                    </Trans>
                  </li>
                  <li>
                    {t(
                      'forms.addressVerification.verificationDocuments.residential.option2.leaseDetail',
                    )}
                  </li>
                </ul>
              </li>
            </ol>
          </AccordionDetails>
        </Accordion>
      </Elevation>
      <Elevation z={2}>
        <Accordion
          className={styles.accordion}
          handle={
            <AccordionSummary
              className={styles.summary}
              text={t(
                'forms.addressVerification.verificationDocuments.smallBusiness.label',
              )}
            />
          }
        >
          <AccordionDetails className={styles.details}>
            <Typography use="headline3">
              {t(
                'forms.addressVerification.verificationDocuments.smallBusiness.option1.title',
              )}
            </Typography>
            <ul>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.smallBusiness.option1.proofOfEmployment"
                  components={{bold: <strong />}}
                >
                  Proof of employment (Letter of Employment) that matches the
                  address on the GFiber account listed <strong>OR</strong>
                </Trans>
              </li>
            </ul>
            <Typography use="headline3">
              {t(
                'forms.addressVerification.verificationDocuments.smallBusiness.option2.title',
              )}
            </Typography>
            <ol>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.smallBusiness.option2.businessCardOrEmail"
                  components={{bold: <strong />}}
                >
                  Business card <strong>OR</strong> business email address
                  domain that matches the current email address domain on file{' '}
                  <strong>OR</strong>
                </Trans>
              </li>
              <li>
                <Trans
                  i18nKey="forms.addressVerification.verificationDocuments.smallBusiness.option2.billOrLeaseAgreement"
                  components={{bold: <strong />}}
                >
                  Bill from the last 60 days (GFiber account address needs to be
                  listed) <strong>OR</strong> lease agreement (GFiber account
                  address needs to be listed).
                </Trans>
              </li>
            </ol>
          </AccordionDetails>
        </Accordion>
      </Elevation>
    </div>
  );
}

export {VerificationDocuments};
